/** Object snap. The thing that makes drafting drafting.
 *
 * ⚠⚠ **THE STATUS BAR HAS BEEN CLAIMING THESE WERE ON SINCE THE DAY THE CHROME WAS LIFTED.**
 * `ENDPOINT MIDPOINT CENTRE INTERSECTION EXTENSION` sat lit on the OSNAP drop-up and nothing
 * snapped. A control that says it is on and does nothing is worse than one that is missing —
 * a drafter trusts it, places a line 3 mm short of the wall, and finds out at the plotter.
 *
 * ⚠⚠ **PRIORITY BEATS PROXIMITY, AND GETTING THAT BACKWARDS BREAKS EVERY OTHER SNAP.** The
 * obvious ranking is "whichever candidate is closest to the cursor wins". It cannot work:
 * NEAREST is a point ON the line, so whenever the cursor is over a line its distance is
 * effectively zero and it wins against every endpoint, midpoint and centre in the aperture. You
 * would never be able to snap to anything else. AutoCAD ranks by KIND first — endpoint, then
 * midpoint, then centre, … then nearest — and only breaks ties within a kind by distance. That
 * is why the order in `PRIORITY` is load-bearing rather than cosmetic.
 *
 * ⚠ Everything here is in MODEL units and the tolerance is passed in, converted from AutoCAD's
 * APERTURE — a pixel box. A tolerance in drawing units would shrink as you zoom in, which is
 * exactly backwards: zooming in is what a drafter does when they need the snap most.
 *
 * ⚠ The index is built from the DISPLAY LIST, so a curve is snapped on its flattened vertices.
 * That is right for endpoint and midpoint of a segment, and it is why CENTRE cannot come from
 * here at all — see `display.py`, which carries circle centres and radii separately because
 * flattening destroys them.
 */
import type { DisplayList } from "./Canvas2D";

export type SnapKind =
  | "endpoint" | "midpoint" | "centre" | "quadrant" | "intersection"
  | "perpendicular" | "nearest" | "extension"
  // ⚠⚠ **THE SIX THAT WERE TICKABLE AND DEAD.** The drop-up listed fourteen and eight worked;
  // `Studio.toggleSnap` said so out loud rather than lighting up and lying, which was the right
  // answer to having them missing and not a reason to leave them missing.
  | "node" | "insertion" | "tangent" | "geocentre" | "apparent"
  | "parallel";

export interface SnapHit { x: number; y: number; kind: SnapKind }

/** ⚠ AutoCAD's own ranking. Lower wins. See the header — this is not a preference. */
const PRIORITY: Record<SnapKind, number> = {
  // ⚠ AutoCAD's own order, which is by how SPECIFIC the point is. An endpoint is one place on
  // one object; nearest is anywhere along it.
  endpoint: 1, midpoint: 2, centre: 3, node: 3.5, quadrant: 4, intersection: 5,
  insertion: 5.5, perpendicular: 6, tangent: 6.5, geocentre: 6.8, nearest: 7,
  // ⚠⚠ The three DEFERRED ones are last, and that is the whole reason they are separate snaps.
  // Each is a hint about where a point WOULD be — the far side of an extension, the crossing of
  // two lines that do not actually cross, a direction parallel to something you hovered. None
  // of them may ever beat a real point that is actually there.
  extension: 8, apparent: 8.5, parallel: 9,
};

/** What this app can actually do, against the fourteen the drop-up lists.
 *  ⚠ Exported so `Studio` can say so when someone ticks one of the other six, rather than
 *  leaving them ticked and silent — this lane's rule is that a dead control says why. */
/** ⚠⚠ **AutoCAD'S `OSMODE` BITMASK — the drawing's own record of which snaps are running.**
 *  The numbering is AutoCAD's and is not menu order; `4133` is its out-of-the-box default
 *  (endpoint + centre + intersection + extension). Mirrored from `sysvars.OSNAP_BITS` on the
 *  server, and the two are asserted equal in the suite so they cannot drift. */
export const OSNAP_BITS: Record<number, SnapKind> = {
  1: "endpoint", 2: "midpoint", 4: "centre", 8: "node", 16: "quadrant",
  32: "intersection", 64: "insertion", 128: "perpendicular", 256: "tangent",
  512: "nearest", 1024: "geocentre", 2048: "apparent", 4096: "extension", 8192: "parallel",
};

/** The bitmask for a list of snap names. */
export function maskOf(names: readonly string[]): number {
  const want = new Set(names);
  let m = 0;
  for (const [bit, name] of Object.entries(OSNAP_BITS)) if (want.has(name)) m |= Number(bit);
  return m;
}

/** The snap names in a bitmask.
 *  ⚠⚠ Bit 16384 is OSNAP OFF — the master toggle F3 flips, stored in the SAME variable. A
 *  drawing saved with snaps off has 16384 added and every other bit still set, so reading it as
 *  just another snap would turn them all on. */
export function namesOf(mask: number): SnapKind[] {
  if (mask & 0x4000) return [];
  const out: SnapKind[] = [];
  for (const [bit, name] of Object.entries(OSNAP_BITS)) if (mask & Number(bit)) out.push(name);
  return out;
}

export const IMPLEMENTED: ReadonlySet<string> = new Set<SnapKind>([
  "endpoint", "midpoint", "centre", "quadrant", "intersection",
  "perpendicular", "nearest", "extension",
  "node", "insertion", "tangent", "geocentre", "apparent", "parallel",
]);

/** The names AutoCAD answers to at a point prompt, for a SINGLE-SHOT override.
 *
 * ⚠⚠ **A RUNNING SNAP IS A SETTING; AN OVERRIDE IS AN ANSWER TO THE QUESTION ON SCREEN.** A
 * drafter who needs one midpoint types `MID`, snaps it, and the running snaps are exactly as
 * they were on the next pick. Without it the only route to a midpoint is to turn MIDPOINT on
 * for the rest of the day and remember to turn it off — which nobody does, so every subsequent
 * pick near a wall grabs its middle instead of its end.
 *
 * ⚠ AutoCAD's own three-letter codes, plus the spellings people actually type. `CEN` maps to
 * `centre` because that is how `catalog.ts` spells the snap; the drafter's spelling and the
 * table's do not have to agree, and only one of them is on screen.
 * ⚠ `NON` is here and is not a snap: it turns snapping OFF for one pick, which is the only way
 * to put a point NEAR a line without landing on it.
 */
export const SNAP_WORDS: Record<string, SnapKind | "none"> = {
  END: "endpoint", ENDP: "endpoint", ENDPOINT: "endpoint",
  MID: "midpoint", MIDP: "midpoint", MIDPOINT: "midpoint",
  CEN: "centre", CENTER: "centre", CENTRE: "centre",
  NOD: "node", NODE: "node",
  QUA: "quadrant", QUAD: "quadrant", QUADRANT: "quadrant",
  INT: "intersection", INTERSECTION: "intersection",
  INS: "insertion", INSERT: "insertion",
  PER: "perpendicular", PERP: "perpendicular", PERPENDICULAR: "perpendicular",
  TAN: "tangent", TANGENT: "tangent",
  NEA: "nearest", NEAR: "nearest", NEAREST: "nearest",
  EXT: "extension", EXTENSION: "extension",
  APP: "apparent", APPINT: "apparent",
  PAR: "parallel", PARALLEL: "parallel",
  GCE: "geocentre",
  NON: "none", NONE: "none",
};

/** Which single-shot snap the drafter typed, or null.
 *
 * ⚠⚠ **ASK THE COMMAND'S OWN KEYWORDS FIRST.** At ARC's `[Center]` prompt `CEN` is the Center
 * OPTION, not the centre snap, and AutoCAD resolves it that way — the option is what the prompt
 * is offering. `coords.matchKeyword` is the one that must run first; this is the fallback.
 */
export function overrideOf(text: string): SnapKind | "none" | null {
  const up = (text || "").trim().toUpperCase().replace(/^[_-]+/, "");
  return up in SNAP_WORDS ? SNAP_WORDS[up] : null;
}

/** ⚠ How far past a segment's end an EXTENSION snap still reaches, in tolerances. AutoCAD
 *  tracks the extension indefinitely once you have hovered the endpoint; without that hover
 *  history a bounded reach is the honest approximation, and an unbounded one would put an
 *  extension marker on a cursor sitting in empty space halfway across the drawing. */
const EXT_REACH = 24;

/** ⚠ Intersections are O(k²) in the segments inside the aperture. A hatch boundary can put
 *  hundreds there, and 300² is 90,000 line-line solves inside a 16 ms budget. Capped. */
const MAX_PAIRS = 40;

export class SnapIndex {
  /** `[x0, y0, x1, y1]` per segment, flat. ⚠ A Float64Array, not an array of objects: a real
   *  plan is 165,000 segments and the object version is 40 MB of pointers. */
  private seg: Float64Array;
  private nseg = 0;
  /** Circle/arc centres, flat `[x, y, r]`. */
  private cen: Float64Array;
  private ncen = 0;
  private cell = 1;
  private nx = 1;
  private ox = 0;
  private oy = 0;
  private grid = new Map<number, number[]>();
  /** ⚠ Segments whose bounding box covers too many cells to insert — a construction line
   *  spanning the whole drawing. Scanned linearly; there are normally a handful. */
  private long: number[] = [];
  /** POINT entities, for the NODE snap. ⚠ Their own channel because a point has no extent —
   *  see `marks` in `display.py`. Flat `[x, y]`. */
  private nodes: number[] = [];
  /** Insertion points of blocks, texts and attributes, for the INSERTION snap. ⚠ Not on any
   *  line they draw: a door block's insertion point is the hinge. Flat `[x, y]`. */
  private inserts: number[] = [];
  /** Centroids of CLOSED poly runs, for the GEOMETRIC CENTRE snap. ⚠ AutoCAD's GCEN is the
   *  centroid of the AREA, not the average of the vertices — those differ on any shape whose
   *  vertices are unevenly spaced, which is every real room. Flat `[x, y]`. */
  private gcen: number[] = [];
  /** ⚠⚠ **THE SEGMENT THE CURSOR LAST HOVERED, WHICH IS WHAT MAKES `parallel` WORK.** AutoCAD's
   *  parallel snap is DEFERRED: you hover a line to acquire it, then move away and the cursor
   *  tracks parallel to it from the base point. Without the acquisition there is nothing to be
   *  parallel TO, and a snap that guessed would put a track through empty space. */
  private acquired: [number, number, number, number] | null = null;

  constructor(dl: DisplayList) {
    let n = 0;
    for (const [li, , pts] of dl.polys) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      n += Math.max(0, pts.length / 2 - 1);
    }
    this.seg = new Float64Array(n * 4);
    const [x0, y0, x1, y1] = dl.extents;
    const area = Math.max(1e-9, (x1 - x0) * (y1 - y0));
    // ⚠ Sized for roughly four segments a cell. Too big and every lookup scans a crowd; too
    // small and a drawing costs millions of empty Map entries.
    this.cell = Math.max(1e-9, Math.sqrt((area * 4) / Math.max(1, n)));
    this.ox = x0;
    this.oy = y0;
    this.nx = Math.max(1, Math.min(4096, Math.ceil((x1 - x0) / this.cell) + 1));
    const ny = Math.max(1, Math.min(4096, Math.ceil((y1 - y0) / this.cell) + 1));
    // ⚠ Recomputed from the CLAMPED dimensions. Clamping nx without widening the cell would
    // fold the right-hand side of a big drawing back onto the left, and a snap would fire on
    // geometry 200 m away that happens to share a cell.
    this.cell = Math.max(this.cell, (x1 - x0) / this.nx, (y1 - y0) / ny);
    // ⚠⚠ **ZERO AREA IS A REAL DRAWING, NOT A DEGENERATE ONE.** A lone horizontal LINE has zero
    // height; a column of POINTs has zero width; one construction line has neither. `area` is
    // then clamped to 1e-9 and `cell` collapses to a few microns of drawing — which does not
    // give a WRONG answer, it gives a correct one after five minutes. Sizing off the longer SIDE
    // as well keeps the cell proportional to the drawing when the area cannot.
    this.cell = Math.max(this.cell, Math.max(x1 - x0, y1 - y0) / 4096);

    for (const [li, , pts] of dl.polys) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen) continue;
      for (let i = 0; i + 3 < pts.length; i += 2) this.add(pts[i], pts[i + 1],
                                                          pts[i + 2], pts[i + 3]);
    }

    for (const m of dl.marks ?? []) {
      const ly = dl.layers[m[0]];
      if (!ly || !ly.on || ly.frozen) continue;
      this.nodes.push(m[2], m[3]);
    }
    const ip = dl.ins ?? [];
    for (let i = 0; i + 2 < ip.length; i += 3) {
      const ly = dl.layers[ip[i]];
      if (!ly || !ly.on || ly.frozen) continue;
      this.inserts.push(ip[i + 1], ip[i + 2]);
    }
    // ⚠ A CLOSED run only — an open polyline has no area and therefore no geometric centre,
    // and offering the average of its vertices would be a different point wearing the name.
    for (const [li, , pts] of dl.polys) {
      const ly = dl.layers[li];
      if (!ly || !ly.on || ly.frozen || pts.length < 8) continue;
      if (Math.abs(pts[0] - pts[pts.length - 2]) > 1e-9
          || Math.abs(pts[1] - pts[pts.length - 1]) > 1e-9) continue;
      const g = areaCentroid(pts);
      if (g) this.gcen.push(g[0], g[1]);
    }

    const c = dl.cen ?? [];
    this.cen = new Float64Array((c.length / 4) * 3);
    for (let i = 0; i + 3 < c.length; i += 4) {
      const ly = dl.layers[c[i]];
      if (!ly || !ly.on || ly.frozen) continue;
      this.cen[this.ncen * 3] = c[i + 1];
      this.cen[this.ncen * 3 + 1] = c[i + 2];
      this.cen[this.ncen * 3 + 2] = c[i + 3];
      this.ncen++;
    }
  }

  private key(ix: number, iy: number) { return iy * this.nx + ix; }

  private add(ax: number, ay: number, bx: number, by: number) {
    const i = this.nseg++;
    this.seg[i * 4] = ax; this.seg[i * 4 + 1] = ay;
    this.seg[i * 4 + 2] = bx; this.seg[i * 4 + 3] = by;
    const c = this.cell;
    const ix0 = Math.floor((Math.min(ax, bx) - this.ox) / c);
    const ix1 = Math.floor((Math.max(ax, bx) - this.ox) / c);
    const iy0 = Math.floor((Math.min(ay, by) - this.oy) / c);
    const iy1 = Math.floor((Math.max(ay, by) - this.oy) / c);
    if ((ix1 - ix0 + 1) * (iy1 - iy0 + 1) > 16) { this.long.push(i); return; }
    for (let iy = iy0; iy <= iy1; iy++) {
      for (let ix = ix0; ix <= ix1; ix++) {
        const k = this.key(ix, iy);
        const cellArr = this.grid.get(k);
        if (cellArr) cellArr.push(i); else this.grid.set(k, [i]);
      }
    }
  }

  /** Segment indices whose cell touches the aperture. ⚠ Deduplicated — a segment spanning four
   *  cells is in four buckets, and testing it four times would make its endpoint look like four
   *  coincident candidates. */
  private near(x: number, y: number, tol: number): number[] {
    const c = this.cell;
    const ix0 = Math.floor((x - tol - this.ox) / c), ix1 = Math.floor((x + tol - this.ox) / c);
    const iy0 = Math.floor((y - tol - this.oy) / c), iy1 = Math.floor((y + tol - this.oy) / c);
    // ⚠⚠⚠ **THE 5-MINUTE MOUSE MOVE — AND THE ONE GUARD THAT MAKES IT IMPOSSIBLE.** Measured
    // 2026-09-07: one XLINE in an empty drawing, and a single `pointermove` handler ran for
    // 328,459 ms with the main thread blocked. The page stayed alive at 60 fps BETWEEN events
    // and every screenshot came back correct, so it read as a hung browser and was chased first
    // as a playwright fault and then as a GPU fault.
    //
    // The walk below is `(2·tol/cell)²` cells. That is a handful when `cell` is sized off a real
    // drawing and 1e11 when the extents have no area — and it is looking through an EMPTY grid
    // the whole time, so no amount of "there is nothing here" ever shortens it. The cell floor
    // above stops the arithmetic collapsing; THIS stops any future collapse from being a freeze.
    //
    // ⚠ Falling back to every segment is not a compromise: the grid is an accelerator, never a
    // source of truth, and the drawings that reach this budget are exactly the ones with almost
    // nothing to scan. A wrong answer is impossible either way — only the speed differs.
    if ((ix1 - ix0 + 1) * (iy1 - iy0 + 1) > 4096) {
      const all = new Set<number>();
      for (let i = 0; i < this.nseg; i++) all.add(i);
      return [...all];
    }
    const seen = new Set<number>();
    for (let iy = iy0; iy <= iy1; iy++)
      for (let ix = ix0; ix <= ix1; ix++)
        for (const i of this.grid.get(this.key(ix, iy)) ?? []) seen.add(i);
    for (const i of this.long) seen.add(i);
    return [...seen];
  }

  /** The best snap under the cursor, or null. `want` is the set of RUNNING snaps — F3 off means
   *  an empty set and this is never called.
   *
   *  ⚠⚠ **`from` IS THE COMMAND'S BASE POINT AND PERPENDICULAR IS MEANINGLESS WITHOUT IT.**
   *  "Perpendicular" is perpendicular FROM somewhere; with no base point the only sensible foot
   *  is the cursor's own, which is the NEAREST snap wearing a different name. The first version
   *  of this file offered both at that point and let priority decide, which made NEAREST
   *  unreachable and put a right-angle marker on a line at no angle to anything. Offer it only
   *  when a command has asked for a point. */
  find(x: number, y: number, tol: number, want: ReadonlySet<string>,
       from?: { x: number; y: number } | null): SnapHit | null {
    if (!want.size) return null;
    const tol2 = tol * tol;
    let best: SnapHit | null = null;
    let bestRank = Infinity;
    let bestD = Infinity;
    const offer = (kind: SnapKind, px: number, py: number) => {
      if (!want.has(kind)) return;
      const d = (px - x) * (px - x) + (py - y) * (py - y);
      if (d > tol2) return;
      const r = PRIORITY[kind];
      // ⚠ Rank first, distance only within a rank. See the header.
      if (r < bestRank || (r === bestRank && d < bestD)) {
        bestRank = r; bestD = d; best = { x: px, y: py, kind };
      }
    };

    const ids = this.near(x, y, tol);
    for (const i of ids) {
      const ax = this.seg[i * 4], ay = this.seg[i * 4 + 1];
      const bx = this.seg[i * 4 + 2], by = this.seg[i * 4 + 3];
      offer("endpoint", ax, ay);
      offer("endpoint", bx, by);
      offer("midpoint", (ax + bx) / 2, (ay + by) / 2);
      const vx = bx - ax, vy = by - ay;
      const len = vx * vx + vy * vy;
      if (len <= 0) continue;
      const t = ((x - ax) * vx + (y - ay) * vy) / len;
      const fx = ax + t * vx, fy = ay + t * vy;
      if (t >= 0 && t <= 1) {
        offer("nearest", fx, fy);
        // ⚠ The foot from the BASE POINT, not from the cursor — see the header. It still has to
        // land on the segment: AutoCAD calls the other thing a deferred perpendicular and draws
        // it differently.
        if (from) {
          const pt = ((from.x - ax) * vx + (from.y - ay) * vy) / len;
          if (pt >= 0 && pt <= 1) offer("perpendicular", ax + pt * vx, ay + pt * vy);
        }
      } else if (Math.abs(t - 0.5) <= 0.5 + (EXT_REACH * tol) / Math.sqrt(len)) {
        // ⚠ Past the end but still within reach — an EXTENSION. The foot is on the segment's
        // infinite line, which is exactly where AutoCAD puts the dotted extension track.
        offer("extension", fx, fy);
      }
    }

    // ⚠⚠ **THE SEGMENT UNDER THE CURSOR IS REMEMBERED, WHICH IS HOW `parallel` IS ACQUIRED.**
    // AutoCAD's parallel snap is deferred: hover a line, move away, and the cursor tracks
    // parallel to it. Recorded on every query so the hover that acquires it is the ordinary one
    // a drafter already makes.
    if (ids.length) {
      let bi = -1, bd = Infinity;
      for (const i of ids) {
        const d = segDist2(this.seg, i, x, y);
        if (d < bd) { bd = d; bi = i; }
      }
      if (bi >= 0 && bd <= tol2) {
        this.acquired = [this.seg[bi * 4], this.seg[bi * 4 + 1],
                         this.seg[bi * 4 + 2], this.seg[bi * 4 + 3]];
      }
    }

    // ⚠ Pairwise, and only inside the aperture. See MAX_PAIRS — a hatch boundary can put
    // hundreds of segments here and the square of that does not fit in a frame.
    if ((want.has("intersection") || want.has("apparent"))
        && ids.length <= MAX_PAIRS) {
      for (let a = 0; a < ids.length; a++) {
        for (let b = a + 1; b < ids.length; b++) {
          const p = cross(this.seg, ids[a], ids[b]);
          if (p) offer("intersection", p[0], p[1]);
          // ⚠⚠ **APPARENT INTERSECTION IS WHERE THE LINES WOULD MEET IF THEY WERE LONGER**, and
          // it is a separate snap in AutoCAD precisely because it is a point that is not there.
          // Two walls that stop short of a corner have one; snapping to it is how you draw the
          // corner. Offered only when a real crossing was not found on the same pair, or every
          // real intersection would have a shadow at the same place.
          else if (want.has("apparent")) {
            const q = crossInfinite(this.seg, ids[a], ids[b]);
            if (q) offer("apparent", q[0], q[1]);
          }
        }
      }
    }

    for (let i = 0; i + 1 < this.nodes.length; i += 2) {
      offer("node", this.nodes[i], this.nodes[i + 1]);
    }
    for (let i = 0; i + 1 < this.inserts.length; i += 2) {
      offer("insertion", this.inserts[i], this.inserts[i + 1]);
    }
    for (let i = 0; i + 1 < this.gcen.length; i += 2) {
      offer("geocentre", this.gcen[i], this.gcen[i + 1]);
    }

    // ⚠⚠ **TANGENT AND PARALLEL BOTH NEED A BASE POINT, EXACTLY AS PERPENDICULAR DOES.** A
    // tangent is a tangent FROM somewhere; with no base point there is no line to be tangent
    // to the circle, and offering the quadrant nearest the cursor instead would be a different
    // snap wearing the name. Same for parallel: parallel to what, through where?
    if (from) {
      for (let i = 0; want.has("tangent") && i < this.ncen; i++) {
        const cx = this.cen[i * 3], cy = this.cen[i * 3 + 1], r = this.cen[i * 3 + 2];
        if (r <= 0) continue;
        const dx = from.x - cx, dy = from.y - cy;
        const d2 = dx * dx + dy * dy;
        // ⚠ A base point INSIDE the circle has no tangent from it. Clamping would land on the
        // rim and draw a line that crosses the circle, which is the opposite of a tangent.
        if (d2 <= r * r) continue;
        const d = Math.sqrt(d2);
        const a = Math.atan2(dy, dx);
        const th = Math.acos(r / d);
        offer("tangent", cx + r * Math.cos(a + th), cy + r * Math.sin(a + th));
        offer("tangent", cx + r * Math.cos(a - th), cy + r * Math.sin(a - th));
      }
      if (want.has("parallel") && this.acquired) {
        const [ax2, ay2, bx2, by2] = this.acquired;
        const vx2 = bx2 - ax2, vy2 = by2 - ay2;
        const L = Math.hypot(vx2, vy2);
        if (L > 1e-9) {
          // The foot of the cursor on the line through `from` in the acquired direction.
          const ux = vx2 / L, uy = vy2 / L;
          const t2 = (x - from.x) * ux + (y - from.y) * uy;
          offer("parallel", from.x + ux * t2, from.y + uy * t2);
        }
      }
    }

    for (let i = 0; i < this.ncen; i++) {
      const cx = this.cen[i * 3], cy = this.cen[i * 3 + 1], r = this.cen[i * 3 + 2];
      offer("centre", cx, cy);
      if (r > 0) {
        // ⚠ AutoCAD's QUADRANT is the four compass points of the circle, not of its bounding
        // box — the same thing for a circle and worth saying because it is not for an ellipse,
        // which is why `display.py` sends r = 0 for one.
        offer("quadrant", cx + r, cy); offer("quadrant", cx - r, cy);
        offer("quadrant", cx, cy + r); offer("quadrant", cx, cy - r);
      }
    }
    return best;
  }
}

/** The squared distance from a point to a segment — used to decide which one the cursor is on
 *  for the PARALLEL snap's acquisition. */
function segDist2(s: Float64Array, i: number, x: number, y: number): number {
  const ax = s[i * 4], ay = s[i * 4 + 1], bx = s[i * 4 + 2], by = s[i * 4 + 3];
  const vx = bx - ax, vy = by - ay;
  const len = vx * vx + vy * vy;
  const t = len > 0 ? Math.max(0, Math.min(1, ((x - ax) * vx + (y - ay) * vy) / len)) : 0;
  const fx = ax + t * vx - x, fy = ay + t * vy - y;
  return fx * fx + fy * fy;
}

/** The centroid of a closed ring, by area — AutoCAD's GCEN.
 *  ⚠⚠ **NOT THE AVERAGE OF THE VERTICES.** They differ on any shape whose vertices are unevenly
 *  spaced, which is every real room: a wall drawn as one long segment and the opposite wall as
 *  six short ones pulls a vertex average right across the room. Returns null for a degenerate
 *  ring, where the area formula divides by zero. */
function areaCentroid(pts: readonly number[]): [number, number] | null {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const x0 = pts[i], y0 = pts[i + 1], x1 = pts[i + 2], y1 = pts[i + 3];
    const f = x0 * y1 - x1 * y0;
    a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f;
  }
  if (Math.abs(a) < 1e-12) return null;
  return [cx / (3 * a), cy / (3 * a)];
}

/** Where two segments' INFINITE lines cross, or null when they are parallel.
 *  ⚠ Used only for APPARENT INTERSECTION — a point that is not on either segment. */
function crossInfinite(s: Float64Array, i: number, j: number): [number, number] | null {
  const ax = s[i * 4], ay = s[i * 4 + 1], bx = s[i * 4 + 2], by = s[i * 4 + 3];
  const cx = s[j * 4], cy = s[j * 4 + 1], dx = s[j * 4 + 2], dy = s[j * 4 + 3];
  const rx = bx - ax, ry = by - ay, sx = dx - cx, sy = dy - cy;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;                 // parallel — no meeting point at all
  const t = ((cx - ax) * sy - (cy - ay) * sx) / den;
  return [ax + t * rx, ay + t * ry];
}

/** Where two segments cross, or null. ⚠ Only a REAL crossing — both parameters inside [0, 1].
 *  AutoCAD calls the other thing "apparent intersection" and asks for it separately. */
function cross(s: Float64Array, i: number, j: number): [number, number] | null {
  const ax = s[i * 4], ay = s[i * 4 + 1], bx = s[i * 4 + 2], by = s[i * 4 + 3];
  const cx = s[j * 4], cy = s[j * 4 + 1], dx = s[j * 4 + 2], dy = s[j * 4 + 3];
  const rx = bx - ax, ry = by - ay, sx = dx - cx, sy = dy - cy;
  const den = rx * sy - ry * sx;
  // ⚠ Parallel or degenerate. Two collinear segments have infinitely many "intersections" and
  // no useful one; returning any of them would put a marker at an arbitrary spot on a wall.
  if (den === 0) return null;
  const t = ((cx - ax) * sy - (cy - ay) * sx) / den;
  const u = ((cx - ax) * ry - (cy - ay) * rx) / den;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return [ax + t * rx, ay + t * ry];
}
